/**
 * StepPlotRefinement Component
 *
 * Second step of the story creation wizard.
 * Chat-based interface for refining the story plot with AI.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { PlotOutline } from '@/shared/api/models'
import { Button } from '@/shared/components/Button/Button'
import { Icon } from '@/shared/components/Icon/Icon'
import { pxToRem } from '@/shared/styles/themes'
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons/faExclamationTriangle'
import { faLightbulb } from '@fortawesome/pro-light-svg-icons/faLightbulb'
import { alpha, Box, CircularProgress, keyframes, styled, Typography } from '@mui/material'

import { ChatInput, ChatMessage, PlotProposalCard, PlotSuggestions } from '../components'
import { createAssistantMessage, createUserMessage, useStreamPlotRefinement } from '../hooks/useStreamPlotRefinement'
import { StoryPlotSuggestion, UIChatMessage } from '../types'

interface StepPlotRefinementProps {
  sessionUuid: string
  onContinue: (plot: PlotOutline) => void
  suggestions: StoryPlotSuggestion[]
}

export const StepPlotRefinement: React.FC<StepPlotRefinementProps> = ({
  sessionUuid,
  onContinue,
  suggestions,
}) => {
  const [messages, setMessages] = useState<UIChatMessage[]>([])
  const [inputText, setInputText] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [currentPlot, setCurrentPlot] = useState<PlotOutline | null>(null)
  const [isPlotAccepted, setIsPlotAccepted] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Streaming hook
  const { startRefinement, isRefining, error } = useStreamPlotRefinement({
    sessionUuid,
    onTokenReceived: (token) => {
      // Update the streaming message with new token
      setMessages((prev) =>
        prev.map((msg) =>
          msg.isStreaming ? { ...msg, content: msg.content + token } : msg,
        ),
      )
    },
    onPlotComplete: (plot) => {
      // Remove empty streaming message or keep it if it has content
      setMessages((prev) => {
        const updated = prev.map((msg) => {
          if (!msg.isStreaming) return msg

          // If message has no content, remove it
          if (!msg.content.trim()) return null

          return { ...msg, isStreaming: false }
        })

        return updated.filter(Boolean) as UIChatMessage[]
      })

      setCurrentPlot(plot as PlotOutline)
    },
    onError: (err) => {
      // Mark message as complete on error
      setMessages((prev) =>
        prev.map((msg) =>
          msg.isStreaming
            ? { ...msg, isStreaming: false, content: msg.content || 'An error occurred.' }
            : msg,
        ),
      )
    },
  })

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Add welcome message on mount
  useEffect(() => {
    const welcomeMessage = createAssistantMessage(
      'Hi! I\'m Pixie, your storytelling assistant. Tell me what kind of story you\'d like to create, or tap the lightbulb for some ideas!',
    )

    setMessages([welcomeMessage])
  }, [])

  const handleSendMessage = useCallback(async () => {
    if (!inputText.trim() || isRefining) return

    // Add user message
    const userMsg = createUserMessage(inputText.trim())
    const assistantMsg = createAssistantMessage('', true)

    setMessages((prev) => [...prev, userMsg, assistantMsg])
    setInputText('')
    setCurrentPlot(null) // Clear previous plot

    // Start streaming
    await startRefinement(inputText.trim())
  }, [inputText, isRefining, startRefinement])

  const handleSelectSuggestion = useCallback(
    (plot: string) => {
      setInputText(plot)
      setShowSuggestions(false)
      // Auto-send after a brief delay so user sees the text
      setTimeout(() => {
        if (!isRefining) {
          const userMsg = createUserMessage(plot)
          const assistantMsg = createAssistantMessage('', true)

          setMessages((prev) => [...prev, userMsg, assistantMsg])
          setInputText('')

          startRefinement(plot)
        }
      }, 100)
    },
    [isRefining, startRefinement],
  )

  const handleAcceptPlot = useCallback(() => {
    if (currentPlot) {
      setIsPlotAccepted(true)
      onContinue(currentPlot)
    }
  }, [currentPlot, onContinue])

  return (
    <StyledContainer>
      <StyledHeader>
        <Typography variant="h5" component="h2">
          Let&apos;s Create Your Story
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Chat with Pixie to refine your story plot
        </Typography>
      </StyledHeader>

      <StyledMessagesArea>
        <StyledMessagesInner>
          {messages.map((message) => (
            <ChatMessage key={message.id} message={message} />
          ))}

          {/* Suggestion prompt — only visible with just the welcome message */}
          {messages.length === 1 && (
            <StyledSuggestionPrompt>
              <Button primary size={'large'} onClick={() => setShowSuggestions(true)}>
                <Icon icon={faLightbulb} sx={{ height: 20, marginRight: '4px' }}/>
                Pick a story idea
              </Button>
              <Typography variant="body2" color="text.secondary">
                or describe your story below
              </Typography>
            </StyledSuggestionPrompt>
          )}

          {/* Show plot proposal if available */}
          {currentPlot && !isRefining && (
            <StyledPlotContainer>
              <PlotProposalCard
                plot={currentPlot}
                onAccept={handleAcceptPlot}
                isAccepted={isPlotAccepted}
                isLoading={false}
              />
            </StyledPlotContainer>
          )}

          {/* Loading indicator */}
          {isRefining && messages.length > 0 && !messages.some((m) => m.isStreaming) && (
            <StyledLoadingIndicator>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Pixie is thinking...
              </Typography>
            </StyledLoadingIndicator>
          )}

          {/* Error display */}
          {error && (
            <StyledError>
              <Icon icon={faExclamationTriangle} />
              <Typography variant="body2">{error}</Typography>
            </StyledError>
          )}

          <div ref={messagesEndRef} />
        </StyledMessagesInner>
      </StyledMessagesArea>

      <StyledInputArea>
        <StyledInputInner>
          <ChatInput
            value={inputText}
            onChange={setInputText}
            onSend={handleSendMessage}
            onShowSuggestions={() => setShowSuggestions(true)}
            disabled={isRefining || isPlotAccepted}
            placeholder="Describe your story idea..."
          />
        </StyledInputInner>
      </StyledInputArea>

      {/* Suggestions Dialog */}
      <PlotSuggestions
        open={showSuggestions}
        onClose={() => setShowSuggestions(false)}
        onSelectSuggestion={handleSelectSuggestion}
        suggestions={suggestions}
      />
    </StyledContainer>
  )
}

const StyledContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: ${pxToRem(500)};
  overflow-x: hidden;
`

const StyledHeader = styled(Box)`
  text-align: center;
  flex-shrink: 0;
  margin-bottom: ${pxToRem(16)};
`

const StyledMessagesArea = styled(Box)`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding: 0;
  padding-bottom: ${pxToRem(80)};
`

const StyledMessagesInner = styled(Box)`
  max-width: ${pxToRem(600)};
  width: 100%;
  margin: 0 auto;
  padding-bottom: ${pxToRem(16)};
`

const StyledPlotContainer = styled(Box)`
  margin: ${pxToRem(8)} 0;
`

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const StyledSuggestionPrompt = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${pxToRem(8)};
  margin-top: ${pxToRem(12)};
  padding: ${pxToRem(20)};
  background-color: ${(props) => props.theme.palette.background.paper};
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: ${pxToRem(16)};
  animation: ${fadeIn} 0.4s ease-out;
`

const StyledLoadingIndicator = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${pxToRem(8)};
  padding: ${pxToRem(8)} ${pxToRem(16)};
`

const StyledError = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${pxToRem(8)};
  padding: ${pxToRem(12)} ${pxToRem(16)};
  background-color: ${(props) => props.theme.palette.error.light}20;
  color: ${(props) => props.theme.palette.error.main};
  border-radius: ${pxToRem(8)};
  margin: ${pxToRem(8)} 0;
`

const StyledInputArea = styled(Box)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  padding: ${pxToRem(12)} ${pxToRem(16)} ${pxToRem(24)} ${pxToRem(16)};
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  background-color: ${(props) => alpha(props.theme.palette.background.paper, 0.75)};
`

const StyledInputInner = styled(Box)`
  max-width: ${pxToRem(600)};
  width: 100%;
  margin: 0 auto;
`

export default StepPlotRefinement
